import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { BRAND } from "../lib/brand";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, CheckCircle2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw !== confirm) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Password updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center">
          <img src={BRAND.logoMark} alt="" className="h-14 w-14 rounded-full" />
          <h1 className="mt-3 font-heading text-lg font-extrabold text-slate-900">{BRAND.name}</h1>
        </div>

        {!token ? (
          <div className="rounded-xl border border-border bg-white p-6 text-center" data-testid="reset-invalid">
            <ShieldAlert className="mx-auto h-10 w-10 text-amber-500" />
            <p className="mt-2 text-sm text-slate-600">This reset link is missing or invalid. Please request a new one from the login page.</p>
            <Button className="mt-4 h-11 w-full" onClick={() => navigate("/login")} data-testid="reset-to-login">Back to login</Button>
          </div>
        ) : done ? (
          <div className="rounded-xl border border-border bg-white p-6 text-center" data-testid="reset-done">
            <CheckCircle2 className="mx-auto h-10 w-10 text-primary" />
            <p className="mt-2 text-sm text-slate-600">Your password has been updated. You can now sign in.</p>
            <Button className="mt-4 h-11 w-full" onClick={() => navigate("/login")} data-testid="reset-go-login">Go to login</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="rounded-xl border border-border bg-white p-6 space-y-4">
            <div>
              <h2 className="font-heading text-xl font-bold text-slate-900">Choose a new password</h2>
              <p className="text-sm text-slate-500">Enter and confirm your new password.</p>
            </div>
            <div className="space-y-1.5">
              <Label>New password</Label>
              <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 6 characters" className="h-11" data-testid="reset-password" required />
            </div>
            <div className="space-y-1.5">
              <Label>Confirm new password</Label>
              <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="h-11" data-testid="reset-confirm" required />
            </div>
            <Button type="submit" className="h-11 w-full" disabled={loading} data-testid="reset-submit">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Update password
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
